# 2025 revision guide

This guide provides important information you need to understand to enable a successful Apply with SEEK Version 2 and new Ad Performance Panel build.

If you are integrating Apply with SEEK for the first time,
refer to the [use case documentation] for guidance instead.

[use case documentation]: /use-cases/apply-with-seek

import { Alert, Text, Stack, Strong, TextLink } from 'braid-design-system';

<Alert tone="caution">
  <Stack space="medium">
    <Text>
      <Strong>Deprecation notice:</Strong> If you currently support Apply with
      SEEK Version 1, note that this version will be deprecated by December
      2025. To ensure you complete migration to Version 2 on time, the deadline
      for submitting your certification request is{' '}
      <Strong>31 October 2025</Strong>.
    </Text>
    <Text>
      For technical support throughout your build, contact the{' '}
      <TextLink href="https://forms.office.com/r/YM37D615Br">
        SEEK Integration Service Desk
      </TextLink>
      .
    </Text>
  </Stack>
</Alert>

## What’s new

### The new Apply with SEEK

View our [feature comparison] for detail on new features in the latest Apply with SEEK and how it compares to the previous version.

[feature comparison]: /guides/2025-revision/awsk-feature-comparison

### Extending your integration with the Ad Performance Panel

If you have integrated Job Posting and the new version of Apply with SEEK, you can optionally include the Ad Performance Panel in your build.
This feature is optional and extends your Apply with SEEK migration with a panel that helps hirers understand how each job ad is performing.
Further guidance on incorporating the Ad Performance Panel is available in this document and the [Ad Performance Panel documentation].

[Ad Performance Panel documentation]: /use-cases/ad-performance/panel

## Integration steps and timeline

The estimated timeline for developing, testing, and releasing Apply with SEEK and the optional Ad Performance Panel typically ranges from 4 weeks to 3 months, depending on your engineering resources, internal system integrations, and change management needs.

import { StepsTimelineTable } from './2025-revision/StepsTimelineTable';

<StepsTimelineTable />

Tasks in the [release](#step-5-release) step can be completed in parallel with other steps.

### Step 1: Understand scope

1. Review [Apply with SEEK] and [Ad Performance Panel] documentation to determine the relevant use cases for your integration
1. Review the [migration considerations] and [hirer identifiers] documentation to finalise your integration scope
1. Ensure your build meets the integration requirements listed below

[Apply with SEEK]: /use-cases/apply-with-seek
[Ad Performance Panel]: /use-cases/ad-performance
[migration considerations]: /guides/2025-revision/awsk-migration-considerations
[hirer identifiers]: /guides/2025-revision/hirer-identifiers

#### Apply with SEEK integration requirements

- **Agreement to terms**: Compliance with the [SEEK API Terms of Use]
- **Mobile optimisation**: Ensure your careers website is optimised for mobile devices
- **Application complete signals**: Implement application complete signals for [all required scenarios]
- **Signal delivery**: Implement [retry logic] for sending application complete signals
- **Hirer identifiers**: Replace legacy SEEK advertiser IDs with [hirer identifiers]
- **Token handling**: Support [retention] of `seek-token`s for 180 days on draft and completed applications
- **Button display**: Display the localised Apply with SEEK button to candidates for all positions advertised on SEEK
- **Button display**: Display the localised Apply with SEEK button to candidates for positions not advertised on SEEK (optional)
- **Apply form pre-fill**: Review the Profile Mapping Document in the Test Steps Workbook and pre-fill all matching profile fields on your apply form to reduce candidate effort

import { TestStepsWorkbook } from './2025-revision/TestStepsWorkbook';

<TestStepsWorkbook />

[all required scenarios]: #application-complete-signals
[retention]: /use-cases/apply-with-seek/send-application-complete-signal#token-lifecycle
[retry logic]: /use-cases/apply-with-seek/send-application-complete-signal#error-handling
[SEEK API Terms of Use]: https://au.employer.seek.com/partners/terms-of-use

#### Ad Performance Panel integration requirements

- **Agreement to terms**: Compliance with the SEEK API Terms of Use
- **Panel implementation**: Able to embed the panel in your web-based software
- **User experience review**: Align your user experience with [our guidelines][app-ux-guidelines]
- **Source attribution**: Ensure consistent tracking of [application source attribution] between SEEK and your software

[app-ux-guidelines]: /use-cases/ad-performance/panel#user-experience
[application source attribution]: #application-complete-signals

### Step 2: Request access

To start your integration, you must submit a [Request Access to Integrate form].
Once reviewed and approved, we will send you the following resources to manage your build:

1. Access to the [Developer Dashboard], where you can manage your SEEK API credentials as well as access features to observe and configure your integration
1. A set of SEEK test hirer accounts, that allow you to post hidden job ads free of charge and retrieve candidate profile information for pre-filling apply forms on your careers website

[Request Access to Integrate form]: https://forms.office.com/r/YM37D615Br

### Step 3: Build & test

#### Developer Dashboard

Before starting your build, familiarise yourself with the [Developer Dashboard], a key tool for managing your integration.
The dashboard allows you to:

- View SEEK hirers that have authorized access to your software
- Create and rotate your client credentials
- Run Playground operations in the GraphQL Explorer

Refer to the Developer Dashboard Guide for further information:

import { DeveloperDashboardGuide } from './2025-revision/DeveloperDashboardGuide';

<DeveloperDashboardGuide />

[Developer Dashboard]: /manage

#### Testing your build

SEEK test hirer accounts provided in Step 2 will allow you to post hidden job ads free of charge via your Job Posting integration or directly through the SEEK Employer website.
Test hirer accounts also allow you to retrieve candidate profile information for pre-filling apply forms on your careers website.

To test your build against the acceptance criteria outlined in the checklist below, you must:

- Use a test hirer account to post link out job ads to SEEK
- Apply to the link out job ads with your self-created test SEEK candidate accounts
- Ensure you are using the correct test hirer account whenever you supply a `hirerId` to the SEEK API

#### Build & test checklists

The following checklists outline requirements and acceptance criteria so you can align your build with our standards and best practices.

import { ApplyWithSeekChecklist } from './2025-revision/ApplyWithSeekChecklist';

<ApplyWithSeekChecklist />

You may optionally use our downloadable Unit Test Cases to guide your internal testing.
These test cases are not used for certification in the next step.

import { UnitTestCases } from './2025-revision/UnitTestCases';

<UnitTestCases />

#### Application complete signals

The table below outlines the scenarios when application complete signals must be sent and whether the application will be attributed to SEEK.
This will ensure your integration is compliant and performs as expected.
For further guidance on handling `seek-token`s and the `sendSignal` mutation, refer to the [application complete signals documentation].

[application complete signals documentation]: /use-cases/apply-with-seek/send-application-complete-signal

import { ApplicationCompleteSignalsTable } from './2025-revision/ApplicationCompleteSignalsTable';

<ApplicationCompleteSignalsTable />

### Step 4: Certification

Certification ensures your integration meets our quality and functionality standards.
This process is divided into two parts, with completion of Part 1 required before your integration can progress to Part 2.

Download the Test Steps Workbook to guide your testing process:

<TestStepsWorkbook />

#### 1. Candidate experience certification

1. Create test job ads
   - Follow the instructions in Part 1 of the Test Steps Workbook to create the 5 required test job ads
   - Record the SEEK URL for each job ad and be ready to share them
1. Prepare for customer migration to the integration
   - Confirm your anticipated integration release date
   - Provide an updated list of hirers currently using Job Posting, Optimised Apply, link out, and/or Apply with SEEK functionality in your software
1. Submit your Part 1 certification request:
   - Submit your request for candidate experience certification via the [SEEK Integration Service Desk]
   - Include URLs for all test job ads created
   - Include your proposed release date and list of hirers to be migrated
1. Feedback and confirmation
   - We will apply to the submitted job ads
   - Feedback will be provided via email if any issues are found
   - Once issues are resolved, we will confirm readiness and provide instructions to proceed to hirer experience certification

#### 2. Hirer experience certification

1. Create video recordings
   - Follow the instructions in Part 2 of the Test Steps Workbook
   - Record videos demonstrating:
     - The applications created against each of the test job ads
     - The applications source attribution tracked in your software
     - The Ad Performance Panel (if applicable)
1. Submit your Part 2 certification request
   - Submit your request for hirer experience certification via the [SEEK Integration Service Desk]
1. Feedback and confirmation
   - We will review your submitted video recordings and provide feedback via email
   - Certification will be granted once all requirements are met

[SEEK Integration Service Desk]: https://forms.office.com/r/YM37D615Br

#### Best practices for a successful certification

- Use exact test data: Ensure the test data provided in the workbook is used without modification to ensure consistency and accuracy during testing
- Demonstrate complete coverage: Certification requires full coverage of all test cases to confirm your integration aligns with our standards
- Resolve feedback promptly: Address any issues identified by SEEK in a timely manner to avoid delays in achieving certification

### Step 5: Release

Once your build has been certified, you are ready to release subject to completing the below parts.

#### 1. Confirm your release plan

- Advise us of your planned release date
- Advise us of your customer release communications approach, including when they are sent to hirers

#### 2. Submit release communications for approval

- SEEK’s Release Pack includes release messaging and branding assets, which will be made available to you once your build has started

  Using the Release Pack, you can easily develop content that showcases the new value you have created for our mutual hirers.

- Before you share your content with hirers, send it to [APIImplementation@seek.com.au](mailto:APIImplementation@seek.com.au) for approval
- We will review your messaging within 2 business days
- Once approved, you are ready to publish it!

#### 3. Confirm your release

- On the day of release, confirm with us that the release has been successfully completed
